import React from 'react'
import { useRouter } from 'next/router'
import { capitalize } from 'lodash'
import { Field, Form } from 'react-final-form'
import { z } from 'zod'
import { Button, InputField } from '@mc/design-system'
import { createValidator } from '@mc/form-utils'
import { Trans, useTranslation } from '@mc/i18n'
import {
  usePersonaPromotion,
  useParsePersonaPromoFeatureVariables,
  PersonaPromoTermsLink,
} from '@mc/persona-promo-utils'
import { FormError } from '../../components/FormError'
import { SocialAuth } from '../../components/SocialAuth'
import { useAuthEvents } from '../../hooks/tracking'
import { i18nInstance } from '../../i18n/i18nInstance'
import { getSocialAuthLink } from '../../utils/getSocialAuthLink'
import {
  AI_TERMS_HREF,
  PRIVACY_POLICY_HREF,
  SPEECH_TO_TEXT_NOTICE_HREF,
  TERMS_OF_SERVICE_HREF,
} from '../../utils/legal'
import { emailFieldSchema } from '../../utils/validationSchemas'

type FormValues = {
  email?: string
}

const tf = i18nInstance.getFixedT(null, '@mc/persona')

const emailSchema = z.object({
  email: emailFieldSchema,
})

const validateEmail = createValidator(emailSchema)

const getArrayJoin = (arr: string[]) => {
  if (arr.length === 0) {
    return ''
  }

  if (arr.length === 1) {
    return arr[0]
  }

  const lastItem = arr.pop()
  return `${arr.join(', ')} ${tf('common.and')} ${lastItem}`
}

interface EmailStepProps {
  initialValues?: { email: string }
  providers?: string[] | null
  isLoading?: boolean
  onEmailSubmit: (values: FormValues) => void
  emailError?: string
  showErrorBox?: boolean
  onChangeEmail?: () => void
  errorMessage?: string
}

export const EmailStep = ({
  initialValues = { email: '' },
  emailError,
  onEmailSubmit,
  providers,
  isLoading,
  onChangeEmail,
  showErrorBox = true,
  errorMessage,
}: EmailStepProps) => {
  const { t } = useTranslation('@mc/persona')
  const { trackSignClick } = useAuthEvents({ step: 1 })
  const { query } = useRouter()

  const { isPromotionOn } = usePersonaPromotion()
  const { parsedVariables } = useParsePersonaPromoFeatureVariables()

  const offerTermsTextKey =
    parsedVariables?.checkout_pages?.signup_page?.offer_terms_text_key

  const handleFormSubmit = ({ email }: FormValues) => {
    if (email) {
      onEmailSubmit({ email })
      trackSignClick({ cta: 'continue with email' })
    }
  }

  const filteredProviders =
    (providers?.filter((prov) => prov !== 'email') as (
      | 'google'
      | 'facebook'
      | 'apple'
    )[]) || []

  const redirectUrl = query.redirect_url as string | undefined
  const fullRedirectUrl =
    redirectUrl && typeof window !== 'undefined'
      ? `${window.location.origin}${redirectUrl}`
      : undefined

  const getEmailError = () => {
    if (emailError) {
      return emailError
    }

    if (filteredProviders.length === 1) {
      return (
        <Trans
          ns='@mc/persona'
          i18nKey='auth.login.existingProvider'
          values={{ provider: capitalize(filteredProviders[0]) }}
        >
          <a
            className='mc-text--link'
            href={getSocialAuthLink(filteredProviders[0], fullRedirectUrl)}
          />
        </Trans>
      )
    }

    if (filteredProviders?.length > 1) {
      return t('auth.login.existingMultipleProviders', {
        providers: getArrayJoin(
          filteredProviders?.map((prov) => capitalize(prov)),
        ),
      })
    }

    return null
  }

  return (
    <>
      <h1 className='mc-text-h2 mc-mb-2'>{t('auth.login.title')}</h1>
      <p className=' mc-mb-8 mc-text-color--medium'>
        {t('auth.login.description')}
      </p>
      <SocialAuth />
      {errorMessage && (
        <FormError error={errorMessage} className='mc-mb-4 mc-text--left' />
      )}
      <Form
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validate={(data) => {
          const errors = validateEmail(data) || {}
          if (emailError) {
            errors.email = emailError
          }
          return errors
        }}
        render={({ handleSubmit, values, pristine, form }) => (
          <form onSubmit={handleSubmit}>
            <Field
              component={InputField}
              id='email'
              name='email'
              type='email'
              required
              placeholder={t('auth.common.email.placeholder')}
              className='mc-mb-3'
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                onChangeEmail?.()
                form.change('email', event.target.value)
              }}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />

            {showErrorBox && !!filteredProviders?.length && (
              <FormError
                error={getEmailError()}
                className='mc-mt-8 mc-text--left'
              />
            )}

            <p className='mc-mt-8 mc-text-small mc-text-color--medium'>
              {isPromotionOn && offerTermsTextKey ? (
                <Trans
                  ns='@mc/persona'
                  i18nKey='auth.common.emailDisclaimerWithOfferTerms'
                >
                  <PersonaPromoTermsLink
                    textKey={offerTermsTextKey}
                    className='mc-text--link'
                  />
                  <a
                    className='mc-text--link'
                    target='_blank'
                    rel='noopener noreferrer'
                    href={TERMS_OF_SERVICE_HREF}
                  />
                  <a
                    className='mc-text--link'
                    target='_blank'
                    rel='noopener noreferrer'
                    href={AI_TERMS_HREF}
                  />
                  <a
                    className='mc-text--link'
                    target='_blank'
                    rel='noopener noreferrer'
                    href={PRIVACY_POLICY_HREF}
                  />
                  <a
                    className='mc-text--link'
                    target='_blank'
                    rel='noopener noreferrer'
                    href={SPEECH_TO_TEXT_NOTICE_HREF}
                  />
                </Trans>
              ) : (
                <Trans ns='@mc/persona' i18nKey='auth.common.emailDisclaimer'>
                  <a
                    className='mc-text--link'
                    target='_blank'
                    rel='noopener noreferrer'
                    href={TERMS_OF_SERVICE_HREF}
                  />
                  <a
                    className='mc-text--link'
                    target='_blank'
                    rel='noopener noreferrer'
                    href={AI_TERMS_HREF}
                  />
                  <a
                    className='mc-text--link'
                    target='_blank'
                    rel='noopener noreferrer'
                    href={PRIVACY_POLICY_HREF}
                  />
                  <a
                    className='mc-text--link'
                    target='_blank'
                    rel='noopener noreferrer'
                    href={SPEECH_TO_TEXT_NOTICE_HREF}
                  />
                </Trans>
              )}
            </p>

            <Button
              type='submit'
              className='mc-mt-8 mc-corners--11'
              disabled={
                !values.email || isLoading || (pristine && !initialValues.email)
              }
              kind='primary'
              fullWidth
            >
              {t('auth.common.cta')}
            </Button>
          </form>
        )}
      />
    </>
  )
}
